import { theme, Avatar, AvatarSize, DropListButton, IconDictionary } from '@ketch-com/deck'
import { Box, Typography, Tooltip } from '@mui/material'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { useState, useEffect } from 'react'
import { ReactFlow, NodeProps, Node, useNodesState, useEdgesState, Handle, Position, useReactFlow } from 'reactflow'
import { TrackersLineageNode } from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { getSystemName } from 'pages/dataSentry/utils/getSystemName'

type CustomNodeProps = { lineageNode: TrackersLineageNode; isFirstNode: boolean; isLastNode: boolean }
type LineageNodeComponent = Node<CustomNodeProps, 'lineageNode'>
type InitialLineageEdges = {
  id: string
  source: string
  target: string
}

const LineageNode: React.FC<NodeProps<CustomNodeProps>> = ({ data }) => {
  const { lineageNode, isFirstNode, isLastNode } = data
  const url = isFirstNode ? lineageNode?.dataSystem?.code : lineageNode?.url || ''
  const dataSystem = lineageNode?.dataSystem || {}
  const dataSystemLogoUrl = dataSystem?.logoUrl || ''
  const dataSystemName = getSystemName(dataSystem || {})
  return (
    <Tooltip title={url}>
      <Box>
        {isFirstNode ? null : <Handle type="target" position={Position.Left} />}
        {isFirstNode ? (
          <Avatar
            icon={IconDictionary.OGlobe}
            borderColor={theme.palette.Black.o8}
            textColor={theme.palette.Black.o48}
            size={AvatarSize.medium}
            sx={{ p: 0.5 }}
          />
        ) : dataSystemLogoUrl ? (
          <Avatar
            src={dataSystemLogoUrl}
            alt={dataSystemName}
            borderColor={theme.palette.Black.o8}
            textColor={theme.palette.Black.o48}
            size={AvatarSize.medium}
            sx={{ p: 0.5 }}
          />
        ) : (
          <Avatar
            icon="NoLogo"
            size={AvatarSize.medium}
            borderColor={theme.palette.Black.o8}
            textColor={theme.palette.Black.o48}
            isLogo
          />
        )}
        {isLastNode ? null : <Handle type="source" position={Position.Right} />}
      </Box>
    </Tooltip>
  )
}

const nodeTypes = {
  lineageNode: LineageNode,
}

export const Lineage: React.FC = () => {
  const { setViewport } = useReactFlow()
  const [selectedLineagePath, setSelectedLineagePath] = useState({ name: 'Path 0', id: 0 })

  const { trackerOverview } = useDataSentryContext()
  const lineages = trackerOverview?.lineages

  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  useEffect(() => {
    const selectedLineage = lineages?.[selectedLineagePath?.id] || []

    const newNodes: LineageNodeComponent[] = selectedLineage.map((lineageNode, i) => ({
      id: String(i),
      position: { x: i * 150, y: 0 },
      type: 'lineageNode',
      data: { lineageNode, isFirstNode: i === 0, isLastNode: i === selectedLineage.length - 1 },
    }))

    const newEdges: InitialLineageEdges[] = selectedLineage
      .map((_, i) =>
        i > 0
          ? {
              id: `e${i - 1}-${i}`,
              source: String(i - 1),
              target: String(i),
            }
          : null,
      )
      .filter(l => l) as InitialLineageEdges[]

    setNodes(newNodes)
    setEdges(newEdges)
    setViewport({ x: 50, y: 200, zoom: 1.5 })
  }, [selectedLineagePath, lineages, setEdges, setNodes, setViewport])

  const options = lineages?.map((_, i) => ({
    name: `Path ${i}`,
    id: i,
  }))

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <DropListButton
        width="180px"
        disableClearable
        value={selectedLineagePath}
        size="small"
        filterOptions={x => x}
        noOptionsText="No Lineages"
        getOptionDisabled={option => selectedLineagePath.id === option.id}
        onChange={(e, path) => {
          if (path) setSelectedLineagePath(path)
        }}
        options={options || []}
        renderOption={(props, path) => {
          return (
            <Box key={path?.id} component="li" {...props} display="flex" alignItems="center" gap={1.5}>
              <Typography variant="label">{path?.name}</Typography>
            </Box>
          )
        }}
        getOptionLabel={system => system?.name || ''}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
      />
      <Box width="80vw" height="60vh" sx={{ margin: '0 auto' }}>
        <ReactFlow
          nodes={nodes}
          onNodesChange={onNodesChange}
          edges={edges}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
        />
      </Box>
    </Box>
  )
}
